import { useNavigateWithSearchParams } from "@p0security/frontend-core";
import {
  type FrontendInstaller,
  Install,
  type InstallSiblings,
} from "@p0security/integrations-installer";
import type { JiraTrackerComponents } from "@p0security/integrations-jira-tracker-shared/components";
import { JiraTrackerRegistration } from "@p0security/integrations-jira-tracker-shared/registry";
import {
  INVALID_SITE_HOST,
  isSiteHost,
} from "@p0security/integrations-jira-tracker-shared/site";
import { Typography } from "@p0security/ui";
import { useCallback } from "react";

import { SiteSteps } from "./install-steps.tsx";
import { NewPreamble } from "./preamble.tsx";
import { TrackerSettings } from "./settings.tsx";

type JiraTrackerInstaller = FrontendInstaller<typeof JiraTrackerComponents>;

const { Text } = Typography;

/**
 * Installs the Jira Cloud site the tracker files findings on. A copy of
 * Jira (HI)'s site installer, less its allowed email domains, with P0
 * holding the OAuth credential.
 *
 * The framework's new-item form takes the site's host. The site's page then
 * shows the steps to create the Atlassian service account, give it the
 * tracker's permissions and create its OAuth credential, above the Next. Its
 * configure page asks for the client ID and the client secret, and Finish
 * validates the site.
 */
const jiraTrackerInstaller: JiraTrackerInstaller = {
  components: {
    site: {
      newPreamble: () => <NewPreamble />,
      // On the new item's form, a component's labeler describes its id.
      labeler: () =>
        `The Jira Cloud site P0 files findings on. ${INVALID_SITE_HOST}`,
      instructions: (_context, id, item) => ({
        help: isSiteHost(id) ? (
          <SiteSteps host={id} site={item} />
        ) : (
          <Text type="warning">
            This site&apos;s ID isn&apos;t a Jira Cloud site&apos;s host. Delete
            this site and add it again. {INVALID_SITE_HOST}
          </Text>
        ),
      }),
      // The framework shows a site's setup instructions only while it's
      // staged, so the configure page shows the steps here. They come from
      // the site P0 stored: the configure form's draft holds what was typed
      // whether or not Finish succeeded.
      preamble: (context, id) => {
        const stored = context.config?.site?.[id];
        if (!stored || stored.state !== "configure" || !isSiteHost(id)) {
          return undefined;
        }
        return <SiteSteps host={id} site={stored} />;
      },
      items: {
        clientId: {
          labeler: () =>
            "The client ID of the service account's OAuth 2.0 credential, from Atlassian Administration.",
        },
        clientSecret: {
          labeler: () =>
            "The client secret of the same credential. P0 stores it encrypted. To rotate it, enter the new one here.",
        },
      },
    },
  },
};

/** The installer with the tracker's settings above its components. */
const pageInstaller: JiraTrackerInstaller = {
  ...jiraTrackerInstaller,
  preamble: <TrackerSettings />,
};

/** The tracker's page: its project settings, then its site's install. */
export const JiraTracker: React.FC<{ siblings: InstallSiblings }> = ({
  siblings,
}) => {
  const navigate = useNavigateWithSearchParams();
  // Finish lands on the tracker's own page, where the project is chosen:
  // the tracker files nothing until then.
  const toTrackerPage = useCallback(() => navigate("."), [navigate]);
  return (
    <Install
      installer={pageInstaller}
      integration="jira-tracker"
      onInstall={toTrackerPage}
      registration={JiraTrackerRegistration}
      siblings={siblings}
    />
  );
};
